import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronsUpDown,
  Download,
  FileClock,
  Filter,
  RefreshCcw,
  Search,
  ShieldCheck,
  Users,
} from "lucide-react";
import { apiUrl } from "@/lib/apiUrl";
import { trpc } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LIFE_BASE_TEMPLATE_TYPES } from "@shared/lifeBaseCatalog";
import {
  getLifeBaseFriendlyErrorMessage,
  getLifeBaseFriendlyOperationalMessage,
} from "@shared/lifeBaseUserFacingErrors";

type ScopedCompany = {
  id: number;
  nomeFantasia?: string | null;
  razaoSocial?: string | null;
};

interface LifeImportHistoryPanelProps {
  scopedCompanies: ScopedCompany[];
  isAdminRole: boolean;
}

function formatDateTime(value?: string | number | Date | null) {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString("pt-BR");
}

function getCompanyLabel(company: ScopedCompany) {
  return company.nomeFantasia || company.razaoSocial || `Empresa #${company.id}`;
}

type SearchableFilterOption = {
  value: string;
  label: string;
  searchText?: string;
};

const HISTORY_STATUS_OPTIONS: SearchableFilterOption[] = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Pendentes" },
  { value: "previewed", label: "Rascunhos preparados" },
  { value: "processing", label: "Em processamento" },
  { value: "completed", label: "Concluídos" },
  { value: "error", label: "Com erro" },
  { value: "discarded", label: "Descartados" },
];

const HISTORY_TEMPLATE_OPTIONS: SearchableFilterOption[] = [
  { value: "all", label: "Todos" },
  { value: LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY, label: "Empresa única" },
  { value: LIFE_BASE_TEMPLATE_TYPES.MULTI_COMPANY, label: "Multiempresa" },
];

function normalizeFilterSearch(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function SearchableFilterSelect({
  open,
  onOpenChange,
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  emptyMessage,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableFilterOption[];
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
}) {
  const selectedOption = options.find(option => option.value === value);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          type="button"
        >
          <span className="truncate text-left">{selectedOption?.label ?? placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command
          filter={(candidate, search) => {
            if (!search) return 1;
            return normalizeFilterSearch(candidate).includes(normalizeFilterSearch(search)) ? 1 : 0;
          }}
        >
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList className="max-h-[300px]">
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.searchText ?? ""}`}
                  onSelect={() => {
                    onValueChange(option.value);
                    onOpenChange(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function getOperationalTone(status?: string | null) {
  const normalized = String(status ?? "").toLowerCase();

  if (
    normalized.includes("concluído")
    || normalized.includes("processado")
    || normalized.includes("sucesso")
  ) {
    return "success" as const;
  }

  if (
    normalized.includes("erro")
    || normalized.includes("pendência")
    || normalized.includes("pendências")
    || normalized.includes("falha")
    || normalized.includes("descartado")
  ) {
    return "danger" as const;
  }

  if (
    normalized.includes("aguardando")
    || normalized.includes("andamento")
    || normalized.includes("processando")
    || normalized.includes("processamento")
    || normalized.includes("validando")
    || normalized.includes("enviando")
    || normalized.includes("enviado")
  ) {
    return "warning" as const;
  }

  return "neutral" as const;
}

function getOperationalBadgeClass(status?: string | null) {
  switch (getOperationalTone(status)) {
    case "success":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "danger":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "warning":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-slate-200 bg-slate-100 text-slate-700";
  }
}

function getVisibleImportStatusLabel(status?: string | null, fallback?: string | null) {
  const candidate = String(status || fallback || "").trim();
  const STATUS_LABEL_MAP: Record<string, string> = {
    "Arquivo recebido": "Aguardando confirmação",
    "Preparando processamento": "Validando arquivo",
    "Aguardando confirmação de envio": "Enviando base",
    "Enviado": "Enviado",
    "Em processamento": "Em processamento",
    "Processando": "Em processamento",
    "Envio não confirmado": "Falha no envio",
    "Concluído": "Concluído",
    "Concluído com pendências": "Concluído com pendências",
    "Erro no processamento": "Erro no processamento",
    "Configuração pendente": "Configuração pendente",
    "Importação com erro": "Importação com erro",
  };

  if (STATUS_LABEL_MAP[candidate]) {
    return STATUS_LABEL_MAP[candidate];
  }

  if (candidate) {
    return candidate;
  }

  return "Sem status";
}

function getRowTone(status?: string | null) {
  switch (status) {
    case "approved":
    case "created":
    case "updated":
    case "reactivated":
    case "inactivated":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "rejected":
    case "error":
      return "border-rose-200 bg-rose-50 text-rose-700";
    default:
      return "border-slate-200 bg-slate-100 text-slate-700";
  }
}

export default function LifeImportHistoryPanel({
  scopedCompanies,
  isAdminRole,
}: LifeImportHistoryPanelProps) {
  const utils = trpc.useUtils();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<
    "all" | "pending" | "previewed" | "processing" | "completed" | "error" | "discarded"
  >("all");
  const [templateType, setTemplateType] = useState<
    "all"
    | (typeof LIFE_BASE_TEMPLATE_TYPES)["SINGLE_COMPANY"]
    | (typeof LIFE_BASE_TEMPLATE_TYPES)["MULTI_COMPANY"]
  >("all");
  const [companyId, setCompanyId] = useState<string>("all");
  const [executorUserId, setExecutorUserId] = useState<string>("all");
  const [statusOpen, setStatusOpen] = useState(false);
  const [templateTypeOpen, setTemplateTypeOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [executorOpen, setExecutorOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const queryInput = useMemo(
    () => ({
      companyId: companyId === "all" ? null : Number(companyId),
      executorUserId: executorUserId === "all" ? null : Number(executorUserId),
      templateType,
      status,
      startDate: startDate || null,
      endDate: endDate || null,
      search: search.trim() || null,
      page,
      pageSize,
    }),
    [companyId, endDate, executorUserId, page, pageSize, search, startDate, status, templateType],
  );

  const historyQuery = trpc.lifeBase.listImportHistory.useQuery(queryInput, {
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
    gcTime: 120_000,
    placeholderData: previousData => previousData,
  });

  const detailsQuery = trpc.lifeBase.getImportHistoryDetails.useQuery(
    {
      jobId: selectedJobId ?? 0,
      rowLimit: 20,
    },
    {
      enabled: selectedJobId !== null,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );

  const refreshStatusMutation = trpc.lifeBase.refreshImportHistoryStatus.useMutation();
  const autoRefreshInFlightRef = useRef(false);

  useEffect(() => {
    setPage(1);
  }, [companyId, executorUserId, search, startDate, endDate, status, templateType]);

  const syncHistoryViews = useCallback(
    async (jobId?: number | null) => {
      await historyQuery.refetch();
      if (selectedJobId !== null && (jobId == null || jobId === selectedJobId)) {
        await detailsQuery.refetch();
      }
      await utils.lifeBase.listImportHistory.invalidate();
      await utils.lifeBase.getImportHistoryDetails.invalidate();
    },
    [detailsQuery, historyQuery, selectedJobId, utils.lifeBase]
  );

  const refreshJobStatus = useCallback(
    async (
      jobId: number,
      options: {
        notify?: boolean;
        syncViews?: boolean;
      } = {}
    ) => {
      const { notify = true, syncViews = true } = options;

      try {
        const data = await refreshStatusMutation.mutateAsync({ jobId });
        if (syncViews) {
          await syncHistoryViews(jobId);
        }
        if (notify) {
          toast.success(data?.message || `Status do lote #${data?.jobId} atualizado.`);
        }
        return data;
      } catch (error) {
        if (notify) {
          toast.error(
            getLifeBaseFriendlyErrorMessage({
              error,
              preferredCategory: "unavailable",
            })
          );
        }
        return null;
      }
    },
    [refreshStatusMutation, syncHistoryViews]
  );

  const records = historyQuery.data?.records ?? [];
  const metrics = historyQuery.data?.metrics ?? {
    total: 0,
    processing: 0,
    completed: 0,
    error: 0,
    multiCompany: 0,
  };
  const availableExecutors = historyQuery.data?.availableExecutors ?? [];
  const companyOptions = useMemo<SearchableFilterOption[]>(() => [
    {
      value: "all",
      label: "Todos os clientes do escopo",
      searchText: "todos clientes escopo",
    },
    ...scopedCompanies.map(company => ({
      value: String(company.id),
      label: getCompanyLabel(company),
      searchText: `${company.nomeFantasia ?? ""} ${company.razaoSocial ?? ""}`,
    })),
  ], [scopedCompanies]);
  const executorOptions = useMemo<SearchableFilterOption[]>(() => [
    {
      value: "all",
      label: "Qualquer executor do escopo",
      searchText: "qualquer executor escopo",
    },
    ...availableExecutors.map(executor => ({
      value: String(executor.id),
      label: executor.name,
      searchText: executor.name,
    })),
  ], [availableExecutors]);
  const currentPage = historyQuery.data?.page ?? page;
  const currentPageSize = historyQuery.data?.pageSize ?? pageSize;
  const hasNextPage = Boolean(historyQuery.data?.hasNextPage);
  const totalRecords = historyQuery.data?.totalRecords ?? null;
  const displayedCount = records.length;
  const cumulativeDisplayedCount = totalRecords !== null
    ? Math.min(totalRecords, Math.max(0, (currentPage - 1) * currentPageSize + displayedCount))
    : displayedCount;
  const totalPages = totalRecords && currentPageSize > 0
    ? Math.max(1, Math.ceil(totalRecords / currentPageSize))
    : null;
  const detailJob = detailsQuery.data?.job ?? null;
  const detailRows = detailsQuery.data?.rows ?? [];
  const detailImpactedCompanies = detailsQuery.data?.impactedCompanies ?? [];
  const detailErrorRows = detailsQuery.data?.errorRows ?? [];
  const detailNeedsAutoRefresh = detailJob?.jobStatus === "processing";
  const autoRefreshJobIds = useMemo(() => {
    const jobIds = new Set<number>();

    for (const record of records) {
      if (record.canRefreshStatus) {
        jobIds.add(record.id);
      }
    }

    if (detailNeedsAutoRefresh && typeof detailJob?.id === "number") {
      jobIds.add(detailJob.id);
    }

    return Array.from(jobIds);
  }, [detailJob?.id, detailNeedsAutoRefresh, records]);

  useEffect(() => {
    if (!hasNextPage) {
      return;
    }

    const nextPageInput = {
      ...queryInput,
      page: currentPage + 1,
    };

    void utils.lifeBase.listImportHistory.prefetch(nextPageInput);
  }, [currentPage, hasNextPage, queryInput, utils.lifeBase.listImportHistory]);

  // `autoRefreshJobIds`, `refreshJobStatus` e `syncHistoryViews` mudam de
  // referência a cada fetch (useQuery/useMutation recriam objetos), e a
  // mutation em si altera `isPending` a cada chamada. Se algum desses entrar
  // nas dependências do efeito abaixo, o próprio disparo do polling reinicia
  // o efeito e gera uma chamada imediata extra, formando um loop
  // autoalimentado (PJC-39). Por isso mantemos os valores voláteis em refs e
  // fazemos o efeito reiniciar apenas quando o conjunto de lotes a
  // acompanhar realmente muda.
  const autoRefreshJobIdsRef = useRef(autoRefreshJobIds);
  const refreshJobStatusRef = useRef(refreshJobStatus);
  const syncHistoryViewsRef = useRef(syncHistoryViews);
  const selectedJobIdRef = useRef(selectedJobId);

  useEffect(() => {
    autoRefreshJobIdsRef.current = autoRefreshJobIds;
  }, [autoRefreshJobIds]);

  useEffect(() => {
    refreshJobStatusRef.current = refreshJobStatus;
  }, [refreshJobStatus]);

  useEffect(() => {
    syncHistoryViewsRef.current = syncHistoryViews;
  }, [syncHistoryViews]);

  useEffect(() => {
    selectedJobIdRef.current = selectedJobId;
  }, [selectedJobId]);

  const autoRefreshJobIdsKey = autoRefreshJobIds.join(",");

  useEffect(() => {
    if (autoRefreshJobIds.length === 0) {
      autoRefreshInFlightRef.current = false;
      return;
    }

    const runAutoRefresh = async () => {
      if (autoRefreshInFlightRef.current) {
        return;
      }

      const jobIds = autoRefreshJobIdsRef.current;
      if (jobIds.length === 0) {
        return;
      }

      autoRefreshInFlightRef.current = true;

      try {
        let refreshedAnyJob = false;

        for (const jobId of jobIds) {
          const result = await refreshJobStatusRef.current(jobId, {
            notify: false,
            syncViews: false,
          });
          refreshedAnyJob = refreshedAnyJob || Boolean(result);
        }

        if (refreshedAnyJob) {
          await syncHistoryViewsRef.current(selectedJobIdRef.current);
        }
      } finally {
        autoRefreshInFlightRef.current = false;
      }
    };

    void runAutoRefresh();
    const intervalId = window.setInterval(() => {
      void runAutoRefresh();
    }, 20_000);

    return () => window.clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reinicia só quando os IDs monitorados mudam (ver comentário acima)
  }, [autoRefreshJobIdsKey]);

  const summaryCards = [
    {
      label: "Lotes no retorno",
      value: metrics.total,
      helper: "Resultado após os filtros ativos",
      icon: FileClock,
    },
    {
      label: "Em acompanhamento",
      value: metrics.processing,
      helper: "Com processamento externo ou confirmação pendente",
      icon: RefreshCcw,
    },
    {
      label: "Concluídos",
      value: metrics.completed,
      helper: "Com retorno operacional concluído",
      icon: ShieldCheck,
    },
    {
      label: "Multiempresa",
      value: metrics.multiCompany,
      helper: "Lotes com abrangência em mais de uma empresa",
      icon: Users,
    },
  ];

  const handleOpenDetails = (jobId: number) => {
    setSelectedJobId(jobId);
    setDetailsOpen(true);
  };

  const handleRefreshStatus = async (jobId: number) => {
    await refreshJobStatus(jobId);
  };

  const renderPaginationControls = (position: "top" | "bottom") => (
    <div className={cn(
      "flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between",
      position === "top" ? "border-b border-slate-200 bg-slate-50/70" : "border-t border-slate-200",
    )}>
      <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-700">
            Exibindo {cumulativeDisplayedCount} de {totalRecords ?? cumulativeDisplayedCount} lote(s)
          </p>

        <p className="text-xs text-slate-500">
          Página {currentPage}{totalPages ? ` de ${totalPages}` : ""}
        </p>
      </div>
      <div className="flex items-center gap-2 self-end sm:self-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(previous => Math.max(1, previous - 1))}
          disabled={currentPage <= 1 || historyQuery.isFetching}
        >
          Anterior
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPage(previous => previous + 1)}
          disabled={!hasNextPage || historyQuery.isFetching}
        >
          Próxima
        </Button>
      </div>
    </div>
  );

  const isStorageProxyUrl = (url: string) => {
    if (url.startsWith("/storage/")) {
      return true;
    }
    try {
      return new URL(url).pathname.startsWith("/storage/");
    } catch {
      return false;
    }
  };

  const buildDownloadHref = (url: string, fileName: string, contentType?: string | null) => {
    if (isStorageProxyUrl(url)) {
      return url;
    }
    const params = new URLSearchParams({ url, fileName });
    if (contentType) {
      params.set("contentType", contentType);
    }
    return apiUrl(`/api/imports/files?${params.toString()}`);
  };

  const buildLifeImportErrorFileName = (sourceFileName: string | null | undefined, jobId: number) => {
    const baseName = (sourceFileName || `lote_${jobId}`).replace(/\.[^.]+$/, "") || `lote_${jobId}`;
    return `${baseName}_erros_lote_${jobId}.csv`;
  };

  const handleDownload = (
    url: string | null | undefined,
    fileName: string | null | undefined,
    emptyMessage?: string,
    contentType?: string | null,
  ) => {
    if (!url || !fileName) {
      toast.info(emptyMessage || "Este lote ainda não possui arquivo disponível para download.");
      return;
    }

    window.open(
      buildDownloadHref(url, fileName, contentType),
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <section className="space-y-6">
      <div className="grid gap-4 xl:grid-cols-4">
        {summaryCards.map(card => {
          const Icon = card.icon;
          return (
            <Card
              key={card.label}
              className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    {card.label}
                  </p>
                  <p className="text-3xl font-semibold tracking-tight text-slate-950">
                    {card.value}
                  </p>
                  <p className="text-sm leading-6 text-slate-600">{card.helper}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-slate-600">
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Card className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Histórico institucional
              </p>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-950">
                  Histórico de importações
                </h2>
                <p className="mt-2 max-w-4xl text-sm leading-7 text-slate-600">
                  Consulte lotes enviados, acompanhe o retorno operacional e abra o detalhe do lote sem precisar navegar até o passo 4 da jornada de importação.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => historyQuery.refetch()}
                disabled={historyQuery.isFetching}
              >
                <RefreshCcw className="h-4 w-4" />
                Atualizar histórico
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  setSearch("");
                  setStatus("all");
                  setTemplateType("all");
                  setCompanyId("all");
                  setExecutorUserId("all");
                  setStartDate("");
                  setEndDate("");
                  setPage(1);
                }}
              >
                <Filter className="h-4 w-4" />
                Limpar filtros
              </Button>
            </div>
          </div>

          <div className="grid gap-4 rounded-[1.5rem] border border-slate-200 bg-slate-50/80 p-4 lg:grid-cols-6">
            <div className="lg:col-span-2">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Buscar lote, arquivo ou executor
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Ex.: lote 345, multiempresa, Maria"
                  className="pl-9"
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Status
                </label>
                {status !== "all" ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-auto px-0 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 hover:bg-transparent hover:text-slate-700"
                    onClick={() => setStatus("all")}
                  >
                    Limpar filtro
                  </Button>
                ) : null}
              </div>
              <SearchableFilterSelect
                open={statusOpen}
                onOpenChange={setStatusOpen}
                value={status}
                onValueChange={value => setStatus(value as typeof status)}
                options={HISTORY_STATUS_OPTIONS}
                placeholder="Todos"
                searchPlaceholder="Buscar status..."
                emptyMessage="Nenhum status encontrado"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Tipo do lote
              </label>
              <SearchableFilterSelect
                open={templateTypeOpen}
                onOpenChange={setTemplateTypeOpen}
                value={templateType}
                onValueChange={value => setTemplateType(value as typeof templateType)}
                options={HISTORY_TEMPLATE_OPTIONS}
                placeholder="Todos"
                searchPlaceholder="Buscar tipo de lote..."
                emptyMessage="Nenhum tipo de lote encontrado"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Cliente
              </label>
              <SearchableFilterSelect
                open={companyOpen}
                onOpenChange={setCompanyOpen}
                value={companyId}
                onValueChange={setCompanyId}
                options={companyOptions}
                placeholder="Todos os clientes do escopo"
                searchPlaceholder="Buscar cliente..."
                emptyMessage="Nenhum cliente encontrado"
              />
            </div>

            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                Executor
              </label>
              <SearchableFilterSelect
                open={executorOpen}
                onOpenChange={setExecutorOpen}
                value={executorUserId}
                onValueChange={setExecutorUserId}
                options={executorOptions}
                placeholder="Qualquer executor do escopo"
                searchPlaceholder="Buscar executor..."
                emptyMessage="Nenhum executor encontrado"
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:col-span-6 xl:grid-cols-[1fr_1fr_auto]">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Período inicial
                </label>
                <Input type="date" value={startDate} onChange={event => setStartDate(event.target.value)} />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Período final
                </label>
                <Input type="date" value={endDate} onChange={event => setEndDate(event.target.value)} />
              </div>
              <div className="flex items-end">
                <Badge variant="outline" className="h-10 rounded-full px-4 text-slate-700">
                  {isAdminRole ? "Visão administrativa" : "Visão no escopo do cliente"}
                </Badge>
              </div>
            </div>
          </div>

          {historyQuery.data?.setupPending ? (
            <Card className="rounded-[1.5rem] border border-amber-200 bg-amber-50 p-5 shadow-none">
              <p className="text-sm leading-7 text-amber-900">
                {historyQuery.data?.setupMessage || "A estrutura persistente da Base de Pessoas ainda está pendente neste ambiente."}
              </p>
            </Card>
          ) : null}

          <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Lotes retornados
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  {totalRecords !== null
                    ? `${cumulativeDisplayedCount} de ${totalRecords} lote(s) no escopo e filtros ativos.`
                    : `${displayedCount} lote(s) carregado(s) na página atual.`}
                </p>
              </div>
              <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">
                Erros mapeados: {metrics.error}
              </Badge>
            </div>

            {!historyQuery.isLoading && records.length > 0 ? renderPaginationControls("top") : null}

            {historyQuery.isLoading ? (
              <div className="space-y-3 p-5">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="h-20 animate-pulse rounded-[1.25rem] bg-slate-100" />
                ))}
              </div>
            ) : records.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-500">
                  <FileClock className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-950">
                    Nenhum lote encontrado
                  </h3>
                  <p className="max-w-2xl text-sm leading-7 text-slate-600">
                    Ajuste os filtros ou selecione outro cliente do escopo para localizar o histórico de importações.
                  </p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Lote</th>
                      <th className="px-5 py-3">Destino</th>
                      <th className="px-5 py-3">Executor</th>
                      <th className="px-5 py-3">Retorno operacional</th>
                      <th className="px-5 py-3">Criado em</th>
                      <th className="px-5 py-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {records.map(record => (
                      <tr key={record.id} className="align-top hover:bg-slate-50/80">
                        <td className="px-5 py-4">
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="font-semibold text-slate-950">Lote #{record.id}</p>
                              <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">
                                {record.templateType === LIFE_BASE_TEMPLATE_TYPES.SINGLE_COMPANY
                                  ? record.importerCompanyName
                                  : record.templateLabel}
                              </Badge>
                              <Badge className={cn("rounded-full border px-3 py-1", getOperationalBadgeClass(record.operationalTracking?.uiStatus || record.jobStatusLabel))}>
                                {getVisibleImportStatusLabel(record.operationalTracking?.uiStatus, record.jobStatusLabel)}
                              </Badge>
                              <Badge
                                variant="outline"
                                className="rounded-full px-3 py-1 text-slate-700"
                                title={record.sourceType === "API" ? "Lote enviado por integração externa via API" : "Lote enviado manualmente pelo Portal"}
                              >
                                Origem: {record.sourceType === "API" ? "API" : "Portal"}
                              </Badge>
                            </div>
                            <div className="space-y-1 text-sm text-slate-600">
                              <p className="font-medium text-slate-700">{record.fileName || "Arquivo sem nome informado"}</p>
                              <p>
                                {record.approvedRows ?? 0} aprovadas · {record.rejectedRows ?? 0} rejeitadas · {record.issueCount ?? 0} ocorrência(s)
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="space-y-1 text-sm text-slate-600">
                            <p className="font-medium text-slate-900">{record.destinationSummary}</p>
                            <p>{record.importerCompanyName}</p>
                            {record.impactedCompaniesPreview?.length ? (
                              <p className="text-xs text-slate-500">
                                {record.impactedCompaniesPreview.join(" · ")}
                              </p>
                            ) : null}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="space-y-1 text-sm text-slate-600">
                            <p className="font-medium text-slate-900">{record.executor?.name || "Executor não identificado"}</p>
                            <p>{record.executor?.email || "Sem e-mail disponível"}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="space-y-2 text-sm text-slate-600">
                              <p className="font-medium text-slate-900">
                                {getLifeBaseFriendlyOperationalMessage(
                                  record.operationalTracking?.message
                                    || record.operationalTracking?.providerStatus
                                    || record.operationalTracking?.uiStatus
                                    || "Sem atualização operacional",
                                  record.operationalTracking?.uiStatus === "Erro no processamento"
                                    ? "processing"
                                    : undefined,
                                )}
                              </p>

                            <div className="flex flex-wrap gap-2">
                              {record.operationalTracking?.processingCode ? (
                                <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">
                                  Proc.: {record.operationalTracking.processingCode}
                                </Badge>
                              ) : null}
                              {record.operationalTracking?.uploadId ? (
                                <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">
                                  Upload: {record.operationalTracking.uploadId}
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600">
                          <div className="space-y-1">
                            <p className="font-medium text-slate-900">{formatDateTime(record.createdAt)}</p>
                            <p>Atualizado em {formatDateTime(record.updatedAt)}</p>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenDetails(record.id)}
                            >
                              Ver detalhe
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRefreshStatus(record.id)}
                              disabled={!record.canManualRefresh || refreshStatusMutation.isPending}
                            >
                              Atualizar status
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!historyQuery.isLoading && records.length > 0 ? renderPaginationControls("bottom") : null}
          </div>
        </div>
      </Card>

      <Sheet open={detailsOpen} onOpenChange={setDetailsOpen}>
        <SheetContent side="right" className="w-full gap-0 border-l border-slate-200 bg-slate-50 p-0 sm:max-w-3xl">
          <SheetHeader className="border-b border-slate-200 bg-white px-6 py-5">
            <SheetTitle className="text-xl tracking-tight text-slate-950">
              {detailJob ? `Lote #${detailJob.id}` : "Detalhe do lote"}
            </SheetTitle>
            <SheetDescription className="text-sm leading-6 text-slate-600">
              Acompanhe o resumo executivo, as empresas impactadas, as linhas amostradas e a trilha operacional do lote.
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-6">
            {detailsQuery.isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-24 animate-pulse rounded-[1.5rem] bg-slate-100" />
                ))}
              </div>
            ) : !detailJob ? (
              <Card className="rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-none">
                <p className="text-sm leading-7 text-slate-600">
                  Selecione um lote para visualizar o detalhe completo.
                </p>
              </Card>
            ) : (
              <div className="space-y-6">
                <Card className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-none">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="rounded-full px-3 py-1 text-slate-700">
                          {detailJob.templateLabel}
                        </Badge>
                        <Badge className={cn("rounded-full border px-3 py-1", getOperationalBadgeClass(detailJob.operationalTracking?.uiStatus || detailJob.jobStatusLabel))}>
                          {getVisibleImportStatusLabel(detailJob.operationalTracking?.uiStatus, detailJob.jobStatusLabel)}
                        </Badge>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-950">
                          {detailJob.fileName || `Lote #${detailJob.id}`}
                        </h3>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          Executor: {detailJob.executor?.name || "Não identificado"} · {detailJob.importerCompanyName}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => handleRefreshStatus(detailJob.id)}
                        disabled={!detailJob.canManualRefresh || refreshStatusMutation.isPending}
                      >
                        <RefreshCcw className="h-4 w-4" />
                        Atualizar status
                      </Button>
                      <Button
                        variant="outline"
                        className="gap-2"
                        onClick={() => handleDownload(
                          detailJob.originalFileUrl ?? detailJob.outputFileUrl,
                          detailJob.fileName,
                          "Este lote ainda não possui arquivo disponível para download.",
                          detailJob.originalFileUrl
                            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            : "text/csv; charset=utf-8",
                        )}
                      >
                        <Download className="h-4 w-4" />
                        Download do arquivo
                      </Button>
                      {detailJob.errorFileUrl ? (
                        <Button
                          variant="outline"
                          className="gap-2"
                            onClick={() => handleDownload(
                              detailJob.errorFileUrl,
                              buildLifeImportErrorFileName(detailJob.fileName, detailJob.id),
                              "Este lote ainda não possui planilha de erros disponível para download.",
                              "text/csv; charset=utf-8",
                            )}

                        >
                          <Download className="h-4 w-4" />
                          Download de erros
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </Card>

                <div className="grid gap-4 md:grid-cols-4">
                  {[
                    {
                      label: "Total de linhas",
                      value: detailJob.totalRows ?? 0,
                    },
                    {
                      label: "Aprovadas",
                      value: detailJob.approvedRows ?? 0,
                    },
                    {
                      label: "Rejeitadas",
                      value: detailJob.rejectedRows ?? 0,
                    },
                    {
                      label: "Processadas",
                      value: detailJob.processedRows ?? 0,
                    },
                  ].map(item => (
                    <Card
                      key={item.label}
                      className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-none"
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                        {item.label}
                      </p>
                      <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
                        {item.value}
                      </p>
                    </Card>
                  ))}
                </div>

                <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                  <Card className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-none">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-semibold text-slate-950">
                          Empresas impactadas
                        </h4>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          O histórico considera empresas diretamente afetadas pelo lote, inclusive nos envios multiempresa.
                        </p>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {detailImpactedCompanies.map(company => (
                          <Badge
                            key={company.id}
                            variant="outline"
                            className={cn(
                              "rounded-full px-3 py-1",
                              company.isContextCompany ? "border-sky-200 bg-sky-50 text-sky-700" : "text-slate-700",
                            )}
                          >
                            {company.name}
                          </Badge>
                        ))}
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Criado em
                          </p>
                          <p className="mt-2 text-sm font-medium text-slate-900">
                            {formatDateTime(detailJob.createdAt)}
                          </p>
                        </div>
                        <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Última atualização
                          </p>
                          <p className="mt-2 text-sm font-medium text-slate-900">
                            {formatDateTime(detailJob.updatedAt)}
                          </p>
                        </div>
                        <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Início do processamento
                          </p>
                          <p className="mt-2 text-sm font-medium text-slate-900">
                            {formatDateTime(detailJob.startedAt)}
                          </p>
                        </div>
                        <div className="rounded-[1.25rem] border border-slate-200 bg-slate-50 p-4">
                          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                            Fim do processamento
                          </p>
                          <p className="mt-2 text-sm font-medium text-slate-900">
                            {formatDateTime(detailJob.finishedAt)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Card>

                  <Card className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-none">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-lg font-semibold text-slate-950">
                          Ocorrências principais
                        </h4>
                        <p className="mt-1 text-sm leading-6 text-slate-600">
                          Amostra rápida das linhas com rejeição ou inconsistência para apoiar a triagem operacional.
                        </p>
                      </div>

                      {detailErrorRows.length === 0 ? (
                        <div className="rounded-[1.25rem] border border-emerald-200 bg-emerald-50 p-4 text-sm leading-7 text-emerald-800">
                          Este lote não possui ocorrências rejeitadas mapeadas no histórico atual.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {detailErrorRows.map(errorRow => (
                            <div
                              key={`${errorRow.rowNumber}-${errorRow.errorCode ?? "sem-codigo"}`}
                              className="rounded-[1.25rem] border border-rose-200 bg-rose-50 p-4"
                            >
                              <p className="text-sm font-semibold text-rose-800">
                                Linha {errorRow.rowNumber}
                              </p>
                              {(errorRow.fullName || errorRow.cpf) ? (
                                <p className="mt-1 text-sm font-medium text-rose-700">
                                  {[errorRow.fullName, errorRow.cpf].filter(Boolean).join(" · ")}
                                </p>
                              ) : null}
                              {(errorRow.companyName || errorRow.planName) ? (
                                <p className="mt-1 text-xs uppercase tracking-[0.12em] text-rose-600">
                                  {[errorRow.companyName, errorRow.planName].filter(Boolean).join(" · ")}
                                </p>
                              ) : null}
                              <p className="mt-2 text-sm leading-6 text-rose-700">
                                {getLifeBaseFriendlyErrorMessage({
                                  error: errorRow.errorMessage,
                                  preferredCategory: "validation",
                                  fallback: "Linha rejeitada sem mensagem operacional detalhada.",
                                  allowValidationDetails: true,
                                })}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                </div>

                <Card className="rounded-[1.5rem] border border-slate-200 bg-white p-5 shadow-none">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-lg font-semibold text-slate-950">
                        Linhas do lote
                      </h4>
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        A visão abaixo mostra uma amostra operacional das primeiras linhas processadas neste lote.
                      </p>
                    </div>

                    <div className="overflow-x-auto rounded-[1.25rem] border border-slate-200">
                      <table className="min-w-full divide-y divide-slate-200 text-sm">
                        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                          <tr>
                            <th className="px-4 py-3">Linha</th>
                            <th className="px-4 py-3">Pessoa</th>
                            <th className="px-4 py-3">Cliente</th>
                            <th className="px-4 py-3">Plano</th>
                            <th className="px-4 py-3">Operação</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 bg-white">
                          {detailRows.map(row => (
                            <tr key={row.id} className="align-top">
                              <td className="px-4 py-3 font-medium text-slate-900">{row.rowNumber}</td>
                              <td className="px-4 py-3 text-slate-600">
                                <div>
                                  <p className="font-medium text-slate-900">{row.fullName || "Pessoa sem nome mapeado"}</p>
                                  <p className="text-xs text-slate-500">
                                    CPF: {row.cpf || "Não informado"}
                                    {row.email ? ` · ${row.email}` : ""}
                                  </p>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-slate-600">{row.companyName}</td>
                              <td className="px-4 py-3 text-slate-600">{row.planName || "Plano não resolvido"}</td>
                              <td className="px-4 py-3 text-slate-600">{row.operationType || "-"}</td>
                              <td className="px-4 py-3">
                                <Badge className={cn("rounded-full border px-3 py-1", getRowTone(row.rowStatus))}>
                                  {row.rowStatus}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </Card>

              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}
